import React, {
  useState,
  useEffect,
  Suspense,
  lazy,
  useCallback,
  useMemo,
  startTransition,
} from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { SnackbarProvider } from "notistack";
import ErrorBoundary from "components/ErrorBoundary/errorBoundary.js";
import PWAInstallPrompt from "components/PWAInstallPrompt";
import UpdatePrompt from "components/UpdatePrompt";
import StaffFeedbackWidget from "components/StaffFeedbackWidget";
import PolicyAcknowledgeModal, { useStaffPolicyModal } from "components/PolicyAcknowledgeModal";

// Contexts & Utility imports
import { profile } from "context/profile";
import { useProfileController, refreshProfile } from "context/profileContext";
import theme from "assets/theme";
import themeDark from "assets/theme-dark";
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";
import { isAuthenticated } from "context/isAuthenticated.js";
import { getToken } from "config/storageController";
import { brandNameProvider } from "./utils/brandName.js";
import { Role_Route, Role_Route_init } from "./utils/role_route.js";
import { PresenceProvider } from "context/presenceContext";
import { useBlockNumberScroll } from "hooks/useBlockNumberScroll";
import { useOnlineStatus } from "hooks/useOnlineStatus";
import { useAttendanceSync } from "hooks/useAttendanceSync";
import { LocaleProvider } from "locale";
import { AcademicYearProvider } from "context/academicYearContext";

// Lazy Loaded Components
const Sidenav = lazy(() => import(/* webpackChunkName: "shell" */ "examples/Sidenav"));
const Configurator = lazy(() => import(/* webpackChunkName: "shell" */ "examples/Configurator"));
const PrivateRoute = lazy(() => import(/* webpackChunkName: "shell" */ "./utils/privateRoute.js"));
const MDBox = lazy(() => import(/* webpackChunkName: "shell" */ "components/MDBox"));
const LoadinSpiner = lazy(() =>
  import(/* webpackChunkName: "shell" */ "components/LoadingSpinner").then((mod) => ({
    default: mod.LoadinSpiner,
  })),
);
const SignIn = lazy(() => import(/* webpackChunkName: "auth" */ "layouts/authentication/sign-in"));
const BranchSelection = lazy(() =>
  import(/* webpackChunkName: "auth" */ "layouts/authentication/sign-in/BranchSelection"),
);
const Forgotten = lazy(() =>
  import(/* webpackChunkName: "auth" */ "layouts/authentication/forgottenpassword"),
);
const AccountRestricted = lazy(() =>
  import(/* webpackChunkName: "auth" */ "layouts/accountRestricted"),
);

// Routes
import routes from "routes";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

const trackPageView = (path) => {
  if (typeof window.gtag === "function") {
    window.gtag("event", "page_view", { page_path: path });
  }
};

function handleChunkError(error) {
  if (!error) return false;
  const msg = (error.message || error.toString() || "").toLowerCase();
  const name = (error.name || "").toLowerCase();
  const isChunkErr =
    name === "chunkyloaderror" ||
    msg.includes("loading chunk") ||
    msg.includes("chunkloaderror") ||
    msg.includes("failed to fetch dynamically imported module") ||
    (msg.includes("unexpected token") && msg.includes("<")) ||
    msg.includes("syntaxerror");

  if (!isChunkErr) return false;

  const alreadyReloaded = sessionStorage.getItem("chunk_reload_global");
  if (alreadyReloaded) {
    sessionStorage.removeItem("chunk_reload_global");
    return false; // stop after one retry to avoid loops
  }

  sessionStorage.setItem("chunk_reload_global", "1");
  Promise.all([
    "serviceWorker" in navigator
      ? navigator.serviceWorker
          .getRegistrations()
          .then((regs) => Promise.all(regs.map((r) => r.unregister())))
      : Promise.resolve(),
    "caches" in window
      ? caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
      : Promise.resolve(),
  ])
    .catch(() => {})
    .finally(() => window.location.reload(true));
  return true;
}

window.addEventListener("unhandledrejection", (event) => {
  if (handleChunkError(event.reason || event)) {
    event.preventDefault(); // suppress the console error
  }
});

window.addEventListener("error", (event) => {
  if (handleChunkError(event.error || event)) {
    event.preventDefault();
  }
});

const RouteLoader = () => (
  <Box
    sx={{
      position: "relative",
      height: "60vh",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: "-60%",
        width: "55%",
        height: "3px",
        borderRadius: "0 3px 3px 0",
        background: "linear-gradient(90deg, transparent, #1A73E8 40%, #42a5f5 70%, transparent)",
        animation: "routeBar 1.6s ease-in-out infinite",
        "@keyframes routeBar": {
          "0%": { left: "-60%" },
          "100%": { left: "110%" },
        },
      }}
    />

    <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 11,
            height: 11,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #1A73E8, #42a5f5)",
            boxShadow: "0 2px 10px rgba(26, 115, 232, 0.45)",
            animation: `routeDot 1.3s ease-in-out ${i * 0.16}s infinite`,
            "@keyframes routeDot": {
              "0%, 100%": { transform: "translateY(0) scale(0.72)", opacity: 0.45 },
              "50%": { transform: "translateY(-14px) scale(1)", opacity: 1 },
            },
          }}
        />
      ))}
    </Box>
  </Box>
);

function getRoutes(allRoutes, allowedRoutes) {
  return allRoutes.flatMap((route) => {
    if (route.collapse) {
      return getRoutes(route.collapse, allowedRoutes);
    }
    if (route.route && allowedRoutes.includes(route.key)) {
      return (
        <Route
          exact
          path={route.route}
          element={<Suspense fallback={<RouteLoader />}>{route.component}</Suspense>}
          key={route.key}
        />
      );
    }
    return [];
  });
}

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const [profileController, profileDispatch] = useProfileController(); //
  const [brandName, setBrandName] = useState(""); //
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();

  const { accepted, accept } = useStaffPolicyModal();
  const [policyOpen, setPolicyOpen] = useState(() => !accepted());
  const handlePolicyAccept = () => {
    accept();
    setPolicyOpen(false);
  };

  let [isAuth, setIsAuth] = useState(false);
  const [authMessage, setAuthMessage] = useState("");
  const [isOpen, setIsOpen] = useState(true);
  const isOnline = useOnlineStatus();

  // Custom hook to block scroll value change on number inputs
  useBlockNumberScroll();

  // Owns the offline attendance queue and syncs it whenever the connection returns
  const { syncPendingAttendance } = useAttendanceSync();

  // Pre-cache critical components for offline support
  useEffect(() => {
    // Pre-cache critical resources when online
    if ("caches" in window && navigator.onLine) {
      caches
        .open("askuala-link-v2")
        .then(() => {
          // Pre-load critical lazy-loaded components so they're cached by service worker
          import("examples/Sidenav").catch((err) =>
            console.warn("Failed to pre-cache Sidenav:", err),
          );
          import("examples/Configurator").catch((err) =>
            console.warn("Failed to pre-cache Configurator:", err),
          );
        })
        .catch((err) => console.warn("Failed to open cache:", err));
    }
  }, []);

  const handleOnMouseEnter = useCallback(() => {
    if (miniSidenav && !onMouseEnter) {
      startTransition(() => {
        setMiniSidenav(dispatch, false);
        setOnMouseEnter(true);
      });
    }
  }, [miniSidenav, onMouseEnter, dispatch]);

  const handleOnMouseLeave = useCallback(() => {
    if (onMouseEnter) {
      startTransition(() => {
        setMiniSidenav(dispatch, true);
        setOnMouseEnter(false);
      });
    }
  }, [onMouseEnter, dispatch]);

  const handleConfiguratorOpen = useCallback(() => {
    startTransition(() => setOpenConfigurator(dispatch, !openConfigurator));
  }, [dispatch, openConfigurator]);

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  const [authLoading, setAuthLoading] = useState(true);
  const checkAuth = useCallback(async () => {
    try {
      if (!navigator.onLine) {
        const localToken = getToken();
        if (localToken) {
          setIsAuth(true);
          refreshProfile(profileDispatch); // Sync local state even when offline
          setAuthMessage("Authenticated (offline mode)");
          setTimeout(() => setIsOpen(false), 2000);
          setAuthLoading(false);
          return;
        }
      }

      // When online, verify with server

      const value = await isAuthenticated();
      setIsAuth(value);
      if (value) {
        refreshProfile(profileDispatch); // Sync React state with the global profile object
        setAuthMessage("Authenticated successfully");
        syncPendingAttendance();
        setTimeout(() => setIsOpen(false), 2000);
      }
    } catch (error) {
      //console.error("Authentication check failed:", error);
      // Fallback to token check if API fails
      const localToken = getToken();
      if (localToken) {
        setIsAuth(true);
      }
    } finally {
      setAuthLoading(false);
    }
  }, [syncPendingAttendance, profileDispatch]);

  const currentRole = profileController.role || profile.role;
  const allowedRoutes = useMemo(() => Role_Route(currentRole), [currentRole]);
  const memoizedRoutes = useMemo(() => getRoutes(routes, allowedRoutes), [routes, allowedRoutes]);

  // Auth check runs once on mount (checkAuth deps are stable after initial load)
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // SPA login does not remount App, so sync the shell as soon as a new session starts.
  const handleAuthLogin = useCallback(() => {
    refreshProfile(profileDispatch);
    setIsAuth(true);
    setAuthMessage("Authenticated successfully");
    setAuthLoading(false);
    setTimeout(() => setIsOpen(false), 2000);
  }, [profileDispatch]);

  useEffect(() => {
    window.addEventListener("auth:login", handleAuthLogin);
    return () => window.removeEventListener("auth:login", handleAuthLogin);
  }, [handleAuthLogin]);

  // Safety net: if a token and role exist but App still thinks logged out, sync on navigation.
  useEffect(() => {
    if (!isAuth && !authLoading && getToken() && (profileController.role || profile.role)) {
      refreshProfile(profileDispatch);
      setIsAuth(true);
    }
  }, [pathname, isAuth, authLoading, profileController.role, profileDispatch]);

  // Page view tracking runs on every navigation without triggering auth
  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);

  // Initial sync for ProfileContext on mount
  useEffect(() => {
    refreshProfile(profileDispatch);
  }, [profileDispatch]);

  return (
    <LocaleProvider>
      <AcademicYearProvider isAuthenticated={isAuth} schoolId={profileController.school_id}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ThemeProvider theme={darkMode ? themeDark : theme}>
            <SnackbarProvider maxSnack={3}>
              <PresenceProvider>
                <CssBaseline />
                <ErrorBoundary>
                  <Suspense fallback={<LoadinSpiner fullScreen />}>
                    {!authLoading && isAuth && pathname !== "/" && (
                      <>
                        <Sidenav
                          color={sidenavColor}
                          brand={
                            (transparentSidenav && !darkMode) || whiteSidenav
                              ? brandDark
                              : brandWhite
                          }
                          brandName={brandNameProvider()}
                          routes={routes}
                          onMouseEnter={handleOnMouseEnter}
                          onMouseLeave={handleOnMouseLeave}
                        />
                      </>
                    )}
                    {(layout === "vr" || layout === "dashboard") && <Configurator />}
                    {isOpen && isAuth && pathname !== "/" && (
                      <Snackbar open={isOpen} autoHideDuration={50}>
                        <Alert
                          severity="success"
                          variant="filled"
                          sx={{ width: "100%" }}
                          onClose={() => setIsOpen(!isOpen)}
                        >
                          {authMessage}
                        </Alert>
                      </Snackbar>
                    )}
                    {!isOnline && (
                      <Snackbar open={!isOnline} autoHideDuration={null}>
                        <Alert severity="error" variant="filled" sx={{ width: "100%" }}>
                          You are currently offline!
                        </Alert>
                      </Snackbar>
                    )}
                    <PWAInstallPrompt />
                    <UpdatePrompt />
                    <StaffFeedbackWidget />
                    <Routes>
                      <Route path="/reset" element={<Forgotten />} />
                      <Route path="/login" element={<SignIn />} />
                      <Route path="/login/branch-selection" element={<BranchSelection />} />
                      <Route path="/account-restricted" element={<AccountRestricted />} />
                      <Route
                        path="*"
                        element={
                          isAuth ? (
                            <Navigate to={Role_Route_init(currentRole)} replace />
                          ) : (
                            <SignIn />
                          )
                        }
                      />
                      <Route element={<PrivateRoute />}>{memoizedRoutes}</Route>
                    </Routes>
                    <PolicyAcknowledgeModal open={policyOpen} onAccept={handlePolicyAccept} />
                  </Suspense>
                </ErrorBoundary>
              </PresenceProvider>
            </SnackbarProvider>
          </ThemeProvider>
        </LocalizationProvider>
      </AcademicYearProvider>
    </LocaleProvider>
  );
}
